import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { missingQuestions, CustomQuestions } from "@/components/wizard/CustomQuestions";
import { StepDetails } from "@/components/wizard/StepDetails";
import { StepPrechecks } from "@/components/wizard/StepPrechecks";
import { StepRide } from "@/components/wizard/StepRide";
import { StepCompetencies } from "@/components/wizard/StepCompetencies";
import { StepOutcome } from "@/components/wizard/StepOutcome";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const STEPS = ["Details", "Pre-checks", "Ride", "Competencies", "Outcome"];
const today = () => new Date().toISOString().slice(0, 10);
const INITIAL = {
  rider_id: "", assessment_type: "initial", date: today(), location: "", bike_details: "", weather: "",
  id_verified: null, id_type: "Driving licence", eyesight_pass: null, disclaimer_signed: false, signature: "", rider_signed_name: "",
  equipment_ok: null, equipment_notes: "", recording_permission: false, roads: [], duration_minutes: null, competencies: {},
  unsafe_behaviour: null, unsafe_details: "", highway_code_asked: false, highway_code_notes: "", refer_to_trustees: false,
  feedback: "", next_steps_explained: false, custom_answers: {},
};
const STEP_KEY = { 1: "prechecks", 2: "ride", 4: "outcome" };

function validate(step, f, cfg) {
  const mq = STEP_KEY[step] ? missingQuestions(cfg.questions, STEP_KEY[step], f.custom_answers) : [];
  const base = validateBase(step, f, cfg);
  if (base) return base;
  if (mq.length) return `Answer required questions: ${mq.map((q) => q.label).join(", ")}`;
  return null;
}

function validateBase(step, f, cfg) {
  if (step === 0 && !f.rider_id) return "Select a rider";
  if (step === 0 && !f.date) return "Enter the assessment date";
  if (step === 1) {
    if (f.id_verified !== true) return "Rider identity must be verified before continuing";
    if (f.eyesight_pass === null) return "Record the eyesight check result";
    if (!f.disclaimer_signed || !f.signature || !f.rider_signed_name) return "Rider must confirm the disclaimer, print their name and sign";
    if (f.equipment_ok === null) return "Record the equipment check";
  }
  if (step === 2 && f.unsafe_behaviour === null) return "Record whether unsafe or illegal behaviour was observed";
  if (step === 3) {
    const missing = cfg.competencies.filter((c) => !f.competencies[c.key]?.score);
    if (missing.length) return `Score all ${cfg.competencies.length} competencies (missing: ${missing.map((c) => c.title).join(", ")})`;
  }
  return null;
}

export default function AssessmentWizard() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [riders, setRiders] = useState([]);
  const [f, setF] = useState({ ...INITIAL, rider_id: params.get("rider") || "" });
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [eyeFail, setEyeFail] = useState(false);
  const [cfg, setCfg] = useState({ competencies: [], questions: [] });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onRider = (id, list = riders) => {
    const r = list.find((x) => x.id === id);
    setF((p) => ({ ...p, rider_id: id, assessment_type: r?.last_pass_date ? "reassessment" : "initial", rider_signed_name: r ? `${r.first_name} ${r.last_name}` : "" }));
  };

  useEffect(() => {
    api.get("/ride-config").then((r) => setCfg(r.data));
    api.get("/riders").then((r) => {
      const list = r.data.filter((x) => x.active);
      setRiders(list);
      if (params.get("rider")) onRider(params.get("rider"), list);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (f.eyesight_pass === false) setEyeFail(true);
  }, [f.eyesight_pass]);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  const next = () => {
    const err = validate(step, f, cfg);
    if (err) return toast.error(err);
    setStep(step + 1);
  };

  const submit = async (data = f) => {
    const err = data === f ? validate(4, f, cfg) : null;
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const { data: a } = await api.post("/assessments", data);
      toast.success("Assessment recorded");
      nav(`/assessments/${a.id}`, { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  const recordCancelled = () => {
    if (!f.rider_id || f.id_verified !== true) return toast.error("Select the rider and verify identity first");
    submit({ ...f, equipment_ok: !!f.equipment_ok, unsafe_behaviour: false, competencies: {} });
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Observed ride</p>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight">New check ride</h1>
      </div>
      <div className="sticky top-[64px] z-30 -mx-4 bg-background/90 px-4 py-3 backdrop-blur-xl lg:top-0" data-testid="wizard-progress">
        <div className="flex gap-1.5">
          {STEPS.map((s, i) => <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-primary" : "bg-secondary"}`} />)}
        </div>
        <div className="mt-2 flex justify-between text-xs text-muted-foreground">
          <span data-testid="wizard-step-label">Step {step + 1} of {STEPS.length} · <span className="font-semibold text-foreground">{STEPS[step]}</span></span>
        </div>
      </div>

      <div key={step} className="rise-in space-y-5">
        {step === 0 && <StepDetails f={f} set={set} riders={riders} onRider={onRider} />}
        {step === 1 && <StepPrechecks f={f} set={set} />}
        {step === 1 && f.eyesight_pass && <CustomQuestions questions={cfg.questions} step="prechecks" f={f} set={set} />}
        {step === 2 && <StepRide f={f} set={set} />}
        {step === 2 && <CustomQuestions questions={cfg.questions} step="ride" f={f} set={set} />}
        {step === 3 && <StepCompetencies f={f} set={set} list={cfg.competencies} />}
        {step === 4 && <StepOutcome f={f} set={set} list={cfg.competencies} />}
        {step === 4 && <CustomQuestions questions={cfg.questions} step="outcome" f={f} set={set} />}
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        <button type="button" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wizard-back-btn" className="inline-flex h-12 items-center gap-2 rounded-full border px-5 text-sm font-semibold disabled:opacity-40">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        {step < STEPS.length - 1 ? (
          <button type="button" onClick={next} data-testid="wizard-next-btn" className="inline-flex h-12 items-center gap-2 rounded-full bg-primary px-7 text-sm font-bold text-primary-foreground">
            Next <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button type="button" disabled={busy} onClick={() => submit()} data-testid="wizard-submit-btn" className="inline-flex h-12 items-center gap-2 rounded-full bg-destructive px-7 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit assessment
          </button>
        )}
      </div>

      <AlertDialog open={eyeFail} onOpenChange={setEyeFail}>
        <AlertDialogContent data-testid="eyesight-fail-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eyesight check failed — assessment cancelled</AlertDialogTitle>
            <AlertDialogDescription>Per the SSCBB procedure, failing the 20 metre number plate check cancels the assessment. Record it now so the Lead Assessor is aware.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="eyesight-fail-cancel" onClick={() => set("eyesight_pass", null)}>Change answer</AlertDialogCancel>
            <AlertDialogAction data-testid="eyesight-fail-record" onClick={recordCancelled} className="bg-destructive text-white hover:bg-red-700">Record cancelled assessment</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
